import React from 'react';
import { Box, Container, Grid, Typography, useTheme, useMediaQuery } from '@mui/material';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

interface FeatureProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  delay: number;
}

const Feature = ({ title, description, icon, delay }: FeatureProps) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.7, delay }}
    >
      <Box
        sx={{
          p: 4,
          height: '100%',
          background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.6) 0%, rgba(15, 23, 42, 0.8) 100%)',
          borderRadius: '16px',
          border: '1px solid rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          display: 'flex',
          flexDirection: 'column',
          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
          '&:hover': {
            transform: 'translateY(-5px)',
            boxShadow: '0 15px 30px rgba(0, 0, 0, 0.2)',
            borderColor: 'rgba(59, 130, 246, 0.3)',
          },
        }}
      >
        <Box
          sx={{
            width: '64px',
            height: '64px',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(37, 99, 235, 0.2) 100%)',
            marginBottom: 3,
            fontSize: '30px',
          }}
        >
          {icon}
        </Box>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            marginBottom: 2,
            background: 'linear-gradient(90deg, #FFFFFF 30%, #94A3B8 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          {title}
        </Typography>
        <Typography variant="body1" sx={{ color: '#94A3B8', lineHeight: 1.7 }}>
          {description}
        </Typography>
      </Box>
    </motion.div>
  );
};

const FeatureHighlight = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const features = [
    {
      title: 'Context-Aware Advertising',
      description: 'Seamlessly integrate relevant ads into LLM responses based on user queries and context, ensuring high engagement and a non-disruptive experience.',
      icon: '🎯',
    },
    {
      title: 'LLM Ad Strategist',
      description: 'Leverage our AI-powered ad strategist to generate optimized advertising plans for different demographics, maximizing your ROI and conversion rates.',
      icon: '🧠',
    },
    {
      title: 'Real-Time Analytics',
      description: 'Monitor performance metrics in real-time with our intuitive dashboard, gaining valuable insights to continuously refine your ad strategy.',
      icon: '📊',
    },
  ];

  return (
    <Box
      sx={{
        py: { xs: 8, md: 12 },
        background: 'linear-gradient(0deg, #0F172A 0%, #1E293B 100%)',
        position: 'relative',
        zIndex: 1,
        overflow: 'hidden',
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <Typography
            variant="h2"
            align="center"
            sx={{
              fontSize: { xs: '2rem', md: '2.75rem' },
              fontWeight: 700,
              marginBottom: 1,
              background: 'linear-gradient(90deg, #FFFFFF 0%, #94A3B8 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Key Features
          </Typography>
          <Typography
            variant="h6"
            align="center"
            sx={{
              color: '#94A3B8',
              marginBottom: 8,
              fontWeight: 400,
              maxWidth: '700px',
              mx: 'auto',
            }}
          >
            Our platform offers everything you need to monetize your AI application effectively
          </Typography>
        </motion.div>

        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Feature
                title={feature.title}
                description={feature.description}
                icon={feature.icon}
                delay={isMobile ? 0 : index * 0.2}
              />
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default FeatureHighlight; 